/**
 * Cashier Redux Slice
 * Manages state for cashier-specific functionality including permissions and session
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../redux/api';

// ==================== Async Thunks ====================

/**
 * Fetch cashier session and permissions
 */
export const fetchCashierSession = createAsyncThunk(
  'cashier/fetchSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/cashier-status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch cashier permissions
 */
export const fetchCashierPermissions = createAsyncThunk(
  'cashier/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/cashier-status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Initial State ====================

const initialState = {
  // Session data
  session: null,
  sessionLoading: false,
  sessionError: null,

  // Permissions - Default to all enabled for testing (will be overridden by backend)
  permissions: {
    voidReturnAuthority: true,
    refundAuthority: true,
    customerSearchPermissions: 'full', // 'none', 'basic', 'full'
    customerCreationRights: true,
    customerProfileEdit: true,
    dataExportRights: true,
    transactionLimit: 1000, // High limit for testing
    shiftHours: null,
  },
  permissionsLoading: false,
  permissionsError: null,

  // Daily transaction tracking
  dailyTransactionCount: 0,
  dailyTransactionLimit: null,

  // Shift status
  isWithinShiftHours: true,
  shiftStartTime: null,
  shiftEndTime: null,
};

// ==================== Slice ====================

const cashierSlice = createSlice({
  name: 'cashier',
  initialState,
  reducers: {
    // Update daily transaction count
    incrementTransactionCount: (state) => {
      state.dailyTransactionCount += 1;
    },

    // Reset daily transaction count (for new day)
    resetDailyTransactionCount: (state) => {
      state.dailyTransactionCount = 0;
    },

    // Update shift status
    updateShiftStatus: (state, action) => {
      state.isWithinShiftHours = action.payload;
    },

    // Set permissions manually
    setPermissions: (state, action) => {
      state.permissions = { ...state.permissions, ...action.payload };
    },

    // Clear cashier session
    clearCashierSession: (state) => {
      state.session = null;
      state.permissions = initialState.permissions;
      state.dailyTransactionCount = 0;
    },

    // Clear errors
    clearError: (state, action) => {
      const errorType = action.payload;
      if (errorType) {
        state[`${errorType}Error`] = null;
      }
    },

    // Reset cashier state
    resetCashierState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch Session
    builder
      .addCase(fetchCashierSession.pending, (state) => {
        state.sessionLoading = true;
        state.sessionError = null;
      })
      .addCase(fetchCashierSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        state.session = action.payload.session || action.payload;
        
        // Update permissions if included in session
        if (action.payload.permissions) {
          state.permissions = { ...state.permissions, ...action.payload.permissions };
        }
        
        // Update daily transaction count if included
        if (action.payload.dailyTransactionCount !== undefined) {
          state.dailyTransactionCount = action.payload.dailyTransactionCount;
        }
        
        // Update shift information if included
        if (action.payload.shiftHours) {
          state.shiftStartTime = action.payload.shiftHours.start;
          state.shiftEndTime = action.payload.shiftHours.end;
        }
        
        if (action.payload.isWithinShiftHours !== undefined) {
          state.isWithinShiftHours = action.payload.isWithinShiftHours;
        }
      })
      .addCase(fetchCashierSession.rejected, (state, action) => {
        state.sessionLoading = false;
        state.sessionError = action.payload;
      });

    // Fetch Permissions
    builder
      .addCase(fetchCashierPermissions.pending, (state) => {
        state.permissionsLoading = true;
        state.permissionsError = null;
      })
      .addCase(fetchCashierPermissions.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.permissions = { ...state.permissions, ...action.payload };
      })
      .addCase(fetchCashierPermissions.rejected, (state, action) => {
        state.permissionsLoading = false;
        state.permissionsError = action.payload;
      });
  },
});

// Export actions
export const {
  incrementTransactionCount,
  resetDailyTransactionCount,
  updateShiftStatus,
  setPermissions,
  clearCashierSession,
  clearError,
  resetCashierState,
} = cashierSlice.actions;

// Export selectors
export const selectCashierSession = (state) => state?.loyalty?.cashier?.session || null;
export const selectCashierPermissions = (state) => state?.loyalty?.cashier?.permissions || initialState.permissions;
export const selectDailyTransactionCount = (state) => state?.loyalty?.cashier?.dailyTransactionCount || 0;
export const selectIsWithinShiftHours = (state) => state?.loyalty?.cashier?.isWithinShiftHours !== undefined ? state.loyalty.cashier.isWithinShiftHours : true;
export const selectSessionLoading = (state) => state?.loyalty?.cashier?.sessionLoading || false;

// Export reducer
export default cashierSlice.reducer;